import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Card, Input, Dropdown, Modal,
  message, Tooltip, Badge, Form, Tag
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  MoreOutlined, UserOutlined, AndroidOutlined, AppleOutlined,
  GlobalOutlined, CopyOutlined, RobotOutlined, WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchApplications, deleteApplication, copyApplication,
  selectApplications, selectApplicationsTotal, selectApplicationsLoading
} from 'store/slices/applications.slice';
import { usePermissions } from 'hooks/usePermissions';
import { Application, AppType, FirebaseConfig } from 'types/application.types';
import { formatters } from 'utils/formatters';
import { DEFAULT_PAGINATION } from 'utils/constants';
import { ROUTES, generatePath } from 'config/routes';
import '../../styles/Common.css';

const { Search } = Input;
const { confirm } = Modal;

const ApplicationList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const applications = useAppSelector(selectApplications);
  const total = useAppSelector(selectApplicationsTotal);
  const isLoading = useAppSelector(selectApplicationsLoading);

  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySource, setCopySource] = useState<Application | null>(null);
  const [copyForm] = Form.useForm();
  const [copyLoading, setCopyLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchText(searchInput);
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadApplications = useCallback(() => {
    const offset = (pagination.current - 1) * pagination.pageSize;
    dispatch(fetchApplications({
      limit: pagination.pageSize,
      offset,
      search: searchText || undefined,
    }));
  }, [dispatch, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleDelete = (app: Application) => {
    confirm({
      title: 'Удаление приложения',
      content: `Вы уверены, что хотите удалить "${app.name}"? Это действие нельзя отменить.`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteApplication(app.id)).unwrap();
          message.success('Приложение успешно удалено');
          loadApplications();
        } catch {
          message.error('Не удалось удалить приложение');
        }
      },
    });
  };

  const openCopyModal = (record: Application) => {
    setCopySource(record);
    setCopyModalOpen(true);
    setTimeout(() => {
      copyForm.setFieldsValue({
        name: `${record.name} (копия)`,
        bundle_id: '',
        firebase_config_json: JSON.stringify(record.firebase_config, null, 2),
      });
    }, 0);
  };

  const handleCopy = async () => {
    if (!copySource) return;
    try {
      const values = await copyForm.validateFields();
      setCopyLoading(true);

      let firebaseConfig: FirebaseConfig;
      try {
        firebaseConfig = JSON.parse(values.firebase_config_json);
      } catch {
        message.error('Невалидный JSON в Firebase конфиге');
        setCopyLoading(false);
        return;
      }

      await dispatch(copyApplication({
        id: copySource.id,
        data: {
          name: values.name,
          bundle_id: values.bundle_id,
          firebase_config: firebaseConfig,
        },
      })).unwrap();

      message.success('Приложение скопировано');
      setCopyModalOpen(false);
      setCopySource(null);
      copyForm.resetFields();
      loadApplications();
    } catch {
      message.error('Не удалось скопировать приложение');
    } finally {
      setCopyLoading(false);
    }
  };

  const getActionMenu = (record: Application): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'view',
        label: 'Просмотр',
        icon: <EyeOutlined />,
        onClick: () => navigate(generatePath(ROUTES.APPLICATION_DETAIL, { id: record.id })),
      },
    ];

    if (permissions.canEditApplications) {
      items.push(
        {
          key: 'edit',
          label: 'Редактировать',
          icon: <EditOutlined />,
          onClick: () => navigate(generatePath(ROUTES.APPLICATION_EDIT, { id: record.id })),
        },
        {
          key: 'users',
          label: 'Пользователи',
          icon: <UserOutlined />,
          onClick: () => navigate(generatePath(ROUTES.APPLICATION_USERS, { id: record.id })),
        }
      );
    }

    if (permissions.canCreateApplications) {
      items.push({
        key: 'copy',
        label: 'Копировать',
        icon: <CopyOutlined />,
        onClick: () => openCopyModal(record),
      });
    }

    if (permissions.canDeleteApplications) {
      items.push(
        { type: 'divider' },
        {
          key: 'delete',
          label: 'Удалить',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => handleDelete(record),
        }
      );
    }

    return items;
  };

  const getAppTypeIcon = (type: AppType) => {
    if (type === AppType.ANDROID_IOS) {
      return (
        <Space>
          <AndroidOutlined style={{ color: '#3ddc84' }} />
          <AppleOutlined style={{ color: '#000' }} />
        </Space>
      );
    }
    return <GlobalOutlined style={{ color: '#1890ff' }} />;
  };

  const columns: ColumnsType<Application> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span className="app-name">{name}</span>,
    },
    {
      title: 'Bundle ID',
      dataIndex: 'bundle_id',
      key: 'bundle_id',
      render: (bundleId: string) => <code className="bundle-id">{bundleId}</code>,
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type: AppType) => (
        <Space>
          {getAppTypeIcon(type)}
          <span>{type === AppType.ANDROID_IOS ? 'Мобильное' : 'PWA'}</span>
        </Space>
      ),
    },
    {
      title: 'Firebase',
      key: 'firebase_access',
      width: 150,
      render: (_, record) => {
        if (!record.auto_created) {
          return <Tag>Вручную</Tag>;
        }
        return record.access_verified ? (
          <Tooltip title={`Проект ${record.firebase_config?.project_id ?? '—'}: доступ подтверждён`}>
            <Tag icon={<RobotOutlined />} color="processing">Автосоздано</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Пуши не уходят: у сервисного аккаунта нет доступа к проекту">
            <Tag icon={<WarningOutlined />} color="warning">Нет доступа</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Пользователи',
      key: 'stats',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Всего пользователей">
            <Badge count={record.user_count || 0} showZero overflowCount={999999}>
              <UserOutlined />
            </Badge>
          </Tooltip>
          <Tooltip title="Активных пользователей">
            <Badge
              count={record.active_users || 0}
              showZero
              overflowCount={999999}
              style={{ backgroundColor: '#52c41a' }}
            >
              <UserOutlined style={{ color: '#52c41a' }} />
            </Badge>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Создано',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => formatters.date(date),
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(generatePath(ROUTES.APPLICATION_DETAIL, { id: record.id }))}
            />
          </Tooltip>
          {permissions.canEditApplications && (
            <Tooltip title="Редактировать">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(generatePath(ROUTES.APPLICATION_EDIT, { id: record.id }))}
              />
            </Tooltip>
          )}
          <Dropdown menu={{ items: getActionMenu(record) }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    console.log('TABLE CHANGE:', newPagination.current, newPagination.pageSize);
    setPagination({
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || DEFAULT_PAGINATION.pageSize,
    });
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <div className="page-header-content">
            <h1>Приложения</h1>
            <p>Управление приложениями для push-уведомлений</p>
          </div>
          <div className="page-header-actions">
            <Search
              placeholder="Поиск приложений..."
              allowClear
              style={{ width: 250 }}
              value={searchInput}
              onSearch={handleSearch}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {permissions.canCreateApplications && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(ROUTES.APPLICATION_CREATE)}
              >
                Создать приложение
              </Button>
            )}
          </div>
        </div>

        <Table
          rowClassName={(record) =>
            record.auto_created && !record.access_verified ? 'row-needs-attention' : ''
          }
          columns={columns}
          dataSource={applications}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} из ${t} записей`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="Копирование приложения"
        open={copyModalOpen}
        onOk={handleCopy}
        onCancel={() => { setCopyModalOpen(false); setCopySource(null); copyForm.resetFields(); }}
        okText="Копировать"
        cancelText="Отмена"
        confirmLoading={copyLoading}
        width={700}
        destroyOnClose
      >
        <Form form={copyForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="bundle_id" label="Bundle ID" rules={[{ required: true, message: 'Введите Bundle ID' }]}>
            <Input placeholder="com.example.newapp" />
          </Form.Item>
          <Form.Item name="firebase_config_json" label="Firebase Config (JSON)" rules={[{ required: true, message: 'Введите конфиг' }]}>
            <Input.TextArea rows={14} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApplicationList;